// Permisos en la web (DOMAIN §11, PER-*): solo para mostrar u ocultar; el servidor vuelve a decidir siempre.
// Delega en @atte/domain: la matriz PER-10 (`PERMISSION_MATRIX`), los módulos de cada acción (PER-05,
// `ACTION_MODULE`, `ACTION_REQUIRES_MODULE_OFF`) y la navegación (PER-06, `NAVIGATION`/`PHONE_ORDER`/`SECTIONS`).
// Acá solo se resuelven las celdas con lo que trae la sesión (`session.locations[].policy`), sin los ajustes
// completos que usa `check` en el servidor.
import type { LocationPolicy, Role } from '@atte/contracts';
import {
  ACTION_MODULE,
  ACTION_REQUIRES_MODULE_OFF,
  NAVIGATION,
  PERMISSION_MATRIX,
  SECTIONS,
  navigationFor as domainNavigationFor,
  phoneBarFor as domainPhoneBarFor,
  sectionActive,
  type Action as DomainAction,
  type Grant,
  type PolicyCondition,
  type Section,
} from '@atte/domain';
import { isModuleActive, type ModuleKey, type ModulesMap } from './modules';
import { useSession } from './session';
import { useOptionalLocation } from './location';

// ─────────────────────────────── secciones (PER-06, UX §2.2) ───────────────────────────────

/** Destinos de navegación; el valor es el segmento de ruta bajo `/app/:loc/` (`SECTIONS[s].route`). */
export type SectionKey =
  | 'agenda'
  | 'caja'
  | 'clientes'
  | 'gift-cards'
  | 'stock'
  | 'comisiones'
  | 'finanzas'
  | 'tablero'
  | 'ajustes'
  | 'ayuda';

const SECTION_LIST = Object.keys(SECTIONS) as Section[];
const routeOf = (s: Section) => SECTIONS[s].route as SectionKey;
const SECTION_BY_ROUTE = Object.fromEntries(SECTION_LIST.map((s) => [routeOf(s), s])) as Record<SectionKey, Section>;

/** Módulo opcional de cada sección (las del núcleo no tienen). */
export const SECTION_MODULE: Partial<Record<SectionKey, ModuleKey>> = Object.fromEntries(
  SECTION_LIST.flatMap((s) => {
    const m = SECTIONS[s].module;
    return m ? [[routeOf(s), m]] : [];
  }),
);

/** ¿La sección está prendida? (su módulo y los que requiere). */
export function sectionEnabled(section: SectionKey, modules: ModulesMap): boolean {
  const s = SECTION_BY_ROUTE[section];
  return !s || sectionActive(s, modules);
}

/** ¿El rol tiene la sección? (sin mirar módulos). "Ajustes" de E y P es solo Mi cuenta. */
export function roleHasSection(role: Role, section: SectionKey): boolean {
  return section === 'ajustes' || NAVIGATION[role].includes(SECTION_BY_ROUTE[section]);
}

/** Destinos del riel (escritorio) para un rol y módulos (PER-06). */
export function navigationFor(role: Role, modules: ModulesMap): SectionKey[] {
  return domainNavigationFor(role, modules).map(routeOf);
}

/**
 * Barra inferior del teléfono (PER-06): hasta 5 lugares; si hay más destinos, el quinto es "Más" con el resto.
 * Las listas son fijas por rol y se filtran por módulos activos.
 */
export function phoneBarFor(role: Role, modules: ModulesMap): { bar: SectionKey[]; more: SectionKey[] } {
  const { bar, more } = domainPhoneBarFor(role, modules);
  return { bar: bar.map(routeOf), more: more.map(routeOf) };
}

// ─────────────────────────────── acciones (PER-10) ───────────────────────────────

export type Action = DomainAction;

/** Parámetros de las celdas condicionales, leídos de la política del local que trae la sesión. */
const POLICY: Record<PolicyCondition, (p: LocationPolicy) => boolean> = {
  'team.staffCanCharge': (p) => p.staffCanCharge,
  'team.staffCanOverbook': (p) => p.staffCanOverbook,
  'team.staffSeesFullPhone': (p) => p.staffSeesFullPhone,
  'sales.priceOverrideMinRole=manager': (p) => p.priceOverrideMinRole === 'manager',
  'cash.managerCanVoid': (p) => p.managerCanVoid,
  'cash.managerCanVoid&!cash.voidSettledRequiresOwner': (p) => p.managerCanVoid && !p.voidSettledRequiresOwner,
  'commissions.managerCanAdvance': (p) => p.managerCanAdvance,
  'giftCards.acceptExpiredRole!=nobody': (p) => p.giftCardAcceptExpiredRole !== 'nobody',
  'giftCards.acceptExpiredRole=manager': (p) => p.giftCardAcceptExpiredRole === 'manager',
};

export interface PermissionCtx {
  userId: string;
  role: Role;
  isAccountHolder: boolean;
  modules: ModulesMap;
  /** Política del local actual (de `session.locations[].policy`). */
  policy: LocationPolicy | null;
  /** Profesional vinculado al usuario en el local actual (PER-03). */
  staffMemberId: string | null;
  /** Vista de solo lectura de otro usuario: nada se puede guardar. */
  readOnly: boolean;
}

export interface CanOptions {
  /** Profesional dueño del recurso (turno, línea) para las reglas "propio". */
  resourceStaffId?: string | null;
  /** La ficha está relacionada con el profesional (CLI-70); el servidor ya filtra, por defecto true. */
  related?: boolean;
  resourceCreatedBy?: string | null;
  clientHasActivity?: boolean;
  targetUserIsOwner?: boolean;
  /** Para acciones de lectura en la vista de solo lectura. */
  readOnlyAllowed?: boolean;
}

const READ_ACTIONS = new Set<Action>([
  'appointment.view',
  'client.view',
  'client.viewContact',
  'cash.view',
  'giftcard.view',
  'stock.view',
  'commission.viewOwn',
  'commission.viewAll',
  'finance.view',
  'dashboard.view',
  'sale.viewCommission',
  'audit.view',
  'booking.link',
]);

function isOwn(c: PermissionCtx, o: CanOptions): boolean {
  if (!c.staffMemberId) return false;
  // Sin recurso (p. ej. "Nuevo turno"), el profesional solo opera sobre sí mismo: se permite.
  return o.resourceStaffId === undefined || o.resourceStaffId === c.staffMemberId;
}

function moduleAllows(action: Action, modules: ModulesMap): boolean {
  const off = ACTION_REQUIRES_MODULE_OFF[action];
  if (off && isModuleActive(modules, off)) return false;
  const m = ACTION_MODULE[action];
  if (!m) return true;
  const list: readonly ModuleKey[] = typeof m === 'string' ? [m] : m;
  return list.some((k) => isModuleActive(modules, k));
}

/** Celda efectiva (sin política del local, las condicionales quedan en ✘). */
function grantOf(ctx: PermissionCtx, action: Action): Exclude<Grant, object> {
  const g = PERMISSION_MATRIX[action][ctx.role];
  if (typeof g === 'string') return g;
  return ctx.policy && POLICY[g.if](ctx.policy) ? g.then : 'no';
}

/** Celdas "limitado" (✔ con un tope o regla propia): la parte que se puede saber antes de pedirlo. */
function limitedAllows(ctx: PermissionCtx, action: Action, o: CanOptions): boolean {
  switch (action) {
    case 'client.delete':
      // CLI-62: con actividad, la anonimiza el dueño.
      return o.clientHasActivity !== true;
    case 'sale.discount': {
      // CAJ-13: el tope del rol tiene que dejar algún descuento.
      const max = ctx.role === 'manager' ? ctx.policy?.discountMaxPctManager : ctx.policy?.discountMaxPctStaff;
      return max == null || max > 0;
    }
    case 'cash.voidMovement':
      // CAJ-74: el encargado anula lo que cargó él.
      return !o.resourceCreatedBy || o.resourceCreatedBy === ctx.userId;
    case 'user.manage':
      // PER-04: el acceso de otro dueño lo cambia solo el titular.
      return !o.targetUserIsOwner || ctx.isAccountHolder;
    default:
      // `cash.expense`: el tope del encargado (CAJ-72) se valida con el importe.
      return true;
  }
}

/** ¿Puede el usuario hacer la acción? (PER-05: primero el módulo, después el rol). */
export function can(ctx: PermissionCtx, action: Action, opts: CanOptions = {}): boolean {
  if (!moduleAllows(action, ctx.modules)) return false;
  if (ctx.readOnly && !READ_ACTIONS.has(action) && !opts.readOnlyAllowed) return false;
  const grant = grantOf(ctx, action);
  switch (grant) {
    case 'yes':
      return true;
    case 'no':
      return false;
    case 'own':
      return isOwn(ctx, opts);
    case 'related':
      return !!ctx.staffMemberId && opts.related !== false;
    case 'limited':
      return limitedAllows(ctx, action, opts);
  }
}

/** Contexto de permisos del usuario en el local actual (o sin local). */
export function usePermissionCtx(): PermissionCtx {
  const s = useSession();
  const loc = useOptionalLocation();
  const actor = s.viewAs ? { role: s.viewAs.role } : { role: s.user.role };
  return {
    userId: s.viewAs?.userId ?? s.user.id,
    role: actor.role,
    isAccountHolder: s.user.isAccountHolder,
    modules: s.modules,
    policy: loc?.policy ?? null,
    staffMemberId: loc?.staffMemberId ?? null,
    readOnly: !!s.viewAs,
  };
}

/** `const can = useCan(); can('sale.void')`. */
export function useCan(): (action: Action, opts?: CanOptions) => boolean {
  const ctx = usePermissionCtx();
  return (action, opts) => can(ctx, action, opts);
}

/** Rol efectivo (el de la vista de solo lectura si está activa). */
export function useRole(): Role {
  return usePermissionCtx().role;
}
